import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./ProjectsGallery.module.css";

const projects = [
  {
    id: 1,
    image: "./assets/images/gallery/installation-8.png",
    alt: "שורת משתנות ללא מים בשירותים ציבוריים גדולים",
    caption: "מתחם שירותים ציבורי",
  },
  {
    id: 2,
    image: "./assets/images/gallery/installation-2.png",
    alt: "משתנות ללא מים לבנות עם מחיצות זכוכית",
    caption: "מסעדה - עיצוב מודרני",
  },
  {
    id: 3,
    image: "./assets/images/gallery/installation-3.png",
    alt: "משתנות ללא מים לבנות במרחב רחב עם אריחים מעוצבים",
    caption: "אולם אירועים",
  },
  {
    id: 4,
    image: "./assets/images/gallery/installation-4.png",
    alt: "משתנות ללא מים כהות בשירותים עם אריחים כהים",
    caption: "מלון בוטיק",
  },
  {
    id: 5,
    image: "./assets/images/gallery/installation-5.png",
    alt: "שורת משתנות ללא מים בשירותים ציבוריים",
    caption: "קניון - שירותים ציבוריים",
  },
  {
    id: 6,
    image: "./assets/images/gallery/installation-6.png",
    alt: "משתנות ללא מים לבנות עם מחיצות",
    caption: "מרכז עסקים",
  },
  {
    id: 7,
    image: "./assets/images/gallery/installation-1.png",
    alt: "משתנות ללא מים כהות מותקנות בשורה",
    caption: "מתחם משרדים",
  },
  {
    id: 8,
    image: "./assets/images/gallery/installation-9.png",
    alt: "משתנות ללא מים לבנות עם מחיצות פרטיות",
    caption: "מוסד ציבורי",
  },
  {
    id: 9,
    image: "./assets/images/gallery/installation-12.png",
    alt: "משתנות ללא מים כהות עם קיר דקורטיבי",
    caption: "מתחם בילוי",
  },
  {
    id: 10,
    image: "./assets/images/gallery/installation-10.png",
    alt: "משתנות ללא מים לבנות בשירותים מעוצבים",
    caption: "משרדי חברת היי-טק",
  },
  {
    id: 11,
    image: "./assets/images/gallery/installation-11.png",
    alt: "שורת משתנות ללא מים בשירותים ציבוריים גדולים",
    caption: "אצטדיון",
  },
];

const ProjectsGallery = () => {
  const railRef = useRef(null);
  const dragState = useRef({ isDown: false, moved: false, startX: 0, startScroll: 0 });

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  // Keep arrow state + progress bar in sync with the scroll position.
  // The rail is RTL, so scrollLeft is 0 at the start and grows negative
  // as the user scrolls toward later items — hence the Math.abs().
  const updateScrollState = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;

    const maxScroll = rail.scrollWidth - rail.clientWidth;
    const scrolled = Math.abs(rail.scrollLeft);
    setProgress(maxScroll > 0 ? scrolled / maxScroll : 0);
    setAtStart(scrolled <= 1);
    setAtEnd(scrolled >= maxScroll - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [updateScrollState]);

  const scrollByStep = useCallback((direction) => {
    const rail = railRef.current;
    if (!rail) return;
    // direction: -1 = toward later items (visually left in RTL), 1 = back toward start.
    const step = rail.clientWidth * 0.8;
    rail.scrollBy({ left: direction * step, behavior: "smooth" });
  }, []);

  // Drag-to-scroll for desktop (pointer based, works for mouse + pen).
  const onPointerDown = useCallback((e) => {
    const rail = railRef.current;
    if (!rail || e.pointerType === "touch") return;
    dragState.current = {
      isDown: true,
      moved: false,
      startX: e.clientX,
      startScroll: rail.scrollLeft,
    };
  }, []);

  const onPointerMove = useCallback((e) => {
    const rail = railRef.current;
    const drag = dragState.current;
    if (!rail || !drag.isDown) return;
    const dx = e.clientX - drag.startX;
    if (Math.abs(dx) > 4) drag.moved = true;
    rail.scrollLeft = drag.startScroll - dx;
  }, []);

  const endDrag = useCallback(() => {
    dragState.current.isDown = false;
  }, []);

  const openLightbox = useCallback((index) => {
    // Ignore the click that ends a drag gesture.
    if (dragState.current.moved) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = "";
  }, []);

  const goToPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev === 0 ? projects.length - 1 : prev - 1));
  }, []);

  const goToNext = useCallback(() => {
    setLightboxIndex((prev) => (prev === projects.length - 1 ? 0 : prev + 1));
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") goToNext();
      if (e.key === "ArrowRight") goToPrev();
    },
    [closeLightbox, goToNext, goToPrev]
  );

  return (
    <section className={styles.gallerySection} aria-label="הפרויקטים שלנו">
      <div className="container-fluid px-3 px-md-5">
        <div className={styles.sectionHeader}>
          <div className={styles.headerText}>
            <h2 className={styles.sectionTitle}>הפרויקטים שלנו</h2>
            <p className={styles.sectionDescription}>
              התקנות במשרדים, מוסדות ומתחמים ציבוריים.
            </p>
          </div>

          <div className={styles.navButtons} aria-hidden="true">
            <button
              type="button"
              className={styles.navButton}
              onClick={() => scrollByStep(1)}
              disabled={atStart}
              aria-label="הקודם"
              tabIndex={-1}
            >
              <span
                className="iconify"
                data-icon="material-symbols:chevron-right-rounded"
              ></span>
            </button>
            <button
              type="button"
              className={styles.navButton}
              onClick={() => scrollByStep(-1)}
              disabled={atEnd}
              aria-label="הבא"
              tabIndex={-1}
            >
              <span
                className="iconify"
                data-icon="material-symbols:chevron-left-rounded"
              ></span>
            </button>
          </div>
        </div>

        <div className={styles.railViewport}>
          <div
            className={styles.rail}
            ref={railRef}
            onScroll={updateScrollState}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
            role="region"
            aria-label="גלריית פרויקטים — ניתן לגלול לצדדים"
            tabIndex={0}
          >
            {projects.map((project, index) => (
              <button
                type="button"
                key={project.id}
                className={styles.galleryItem}
                onClick={() => openLightbox(index)}
                aria-label={`פתח תמונה: ${project.caption}`}
              >
                <img
                  src={project.image}
                  alt={project.alt}
                  className={styles.galleryImage}
                  loading="lazy"
                  decoding="async"
                  draggable="false"
                />
                <div className={styles.galleryOverlay}>
                  <span className={styles.galleryCaption}>{project.caption}</span>
                  <span
                    className={`iconify ${styles.zoomIcon}`}
                    data-icon="material-symbols:zoom-in-rounded"
                    aria-hidden="true"
                  ></span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div
          className={styles.progressTrack}
          role="presentation"
          aria-hidden="true"
        >
          <div
            className={styles.progressThumb}
            style={{ transform: `scaleX(${Math.max(progress, 0.08)})` }}
          ></div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className={styles.lightbox}
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-modal="true"
          aria-label="תצוגת תמונה מוגדלת"
          tabIndex={-1}
          ref={(el) => el && el.focus()}
        >
          <div
            className={styles.lightboxContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={styles.lightboxClose}
              onClick={closeLightbox}
              aria-label="סגור תצוגה"
            >
              <span
                className="iconify"
                data-icon="material-symbols:close-rounded"
                aria-hidden="true"
              ></span>
            </button>

            <button
              type="button"
              className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
              onClick={goToPrev}
              aria-label="תמונה קודמת"
            >
              <span
                className="iconify"
                data-icon="material-symbols:chevron-right-rounded"
                aria-hidden="true"
              ></span>
            </button>

            <img
              src={projects[lightboxIndex].image}
              alt={projects[lightboxIndex].alt}
              className={styles.lightboxImage}
            />

            <button
              type="button"
              className={`${styles.lightboxNav} ${styles.lightboxNext}`}
              onClick={goToNext}
              aria-label="תמונה הבאה"
            >
              <span
                className="iconify"
                data-icon="material-symbols:chevron-left-rounded"
                aria-hidden="true"
              ></span>
            </button>

            <div className={styles.lightboxCaption}>
              <span>{projects[lightboxIndex].caption}</span>
              <span className={styles.lightboxCounter}>
                {lightboxIndex + 1} / {projects.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProjectsGallery;
